export type CreatorSafetyColor = "success" | "warning" | "error";

export type CreatorSafetyStatus = {
  label: "Relatively Safe" | "Potentially Risky" | "Potentially Unsafe";
  color: CreatorSafetyColor;
  tooltip: string;
};

/** Fields required to derive a creator's tier-based safety status. */
export type CreatorSafetyInput = {
  /** Number of the creator's videos flagged with a tier 1 (most severe) risk. */
  videosWithTier1: number;
  /** Percentage (0–100) of the creator's videos flagged with a tier 2 risk. */
  percentageTier2AtRisk: number;
};

/** Percentage of tier 2 videos at or above which a creator is "Potentially Risky". */
export const TIER2_RISKY_THRESHOLD = 20;

/**
 * Single source of truth for a creator's safety status (UNB-1521).
 *
 * Both the creator classification card and the Safety tab derive their status
 * from this function, so the two surfaces can never disagree.
 *
 * - Potentially Unsafe (red)  — at least one tier 1 flagged video
 * - Potentially Risky (amber) — 20% or more of videos flagged as tier 2
 * - Relatively Safe (green)   — everything else
 */
export function getCreatorSafetyStatus({
  videosWithTier1,
  percentageTier2AtRisk,
}: CreatorSafetyInput): CreatorSafetyStatus {
  if (videosWithTier1 > 0) {
    return {
      label: "Potentially Unsafe",
      color: "error",
      tooltip: "This creator has at least one video flagged for a tier 1 risk",
    };
  }
  if (percentageTier2AtRisk >= TIER2_RISKY_THRESHOLD) {
    return {
      label: "Potentially Risky",
      color: "warning",
      tooltip: "20% or more of this creator's videos contain tier 2 risks",
    };
  }
  return {
    label: "Relatively Safe",
    color: "success",
    tooltip: "Fewer than 20% of this creator's videos contain tier 2 risks",
  };
}
